import React, { useCallback, useEffect, useMemo, useState } from 'react';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import MovieIcon from '@mui/icons-material/Movie';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import TableChartIcon from '@mui/icons-material/TableChart';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { ImageIcon } from 'components/icons';
import {
  deleteProjectExportedFile,
  getProjectExportedFile,
  getProjectExportedFiles,
  updateProjectExportedFile,
} from '../../api/projectApi';
import GridFilesPanel, { type GridFileItem } from './GridFilesPanel';
import { buildDownloadFileName, triggerBlobDownload } from './fileDownload';

type ExportedFilesProps = {
  projectId: number | string | null;
  showDeletedItems?: boolean;
  grids?: Array<{ id: number | string; name?: string | null; deletedAt?: string | Date | null }>;
};

const TYPE_METADATA = {
  SCREENSHOT: {
    icon: ImageIcon,
    label: 'Screenshot',
  },
  ANIMATED_360_ORBIT: {
    icon: MovieIcon,
    label: '360 Orbit Animation',
  },
  ANIMATED_360_LOOK_AROUND: {
    icon: MovieIcon,
    label: '360 Look Around Animation',
  },
  ANIMATED_TIMELAPSE: {
    icon: MovieIcon,
    label: 'Timelapse Animation',
  },
  ANIMATED_SCREEN_RECORDING: {
    icon: MovieIcon,
    label: 'Screen Recording',
  },
  '3D_3DM': {
    icon: ViewInArIcon,
    label: '3DM Model',
  },
  '3D_OBJ': {
    icon: ViewInArIcon,
    label: 'OBJ Model',
  },
  '3D_STLF': {
    icon: ViewInArIcon,
    label: 'STL Model',
  },
  '3D_GLTF': {
    icon: ViewInArIcon,
    label: 'glTF Model',
  },
  CSV_DATA: {
    icon: TableChartIcon,
    label: 'CSV Data',
  },
  TSV_DATA: {
    icon: TableChartIcon,
    label: 'TSV Data',
  },
  JSON_DATA: {
    icon: InsertDriveFileIcon,
    label: 'JSON Data',
  },
};

const canDownload = (file?: GridFileItem | null) =>
  Boolean(file) && !['EXPORTING', 'FAILED'].includes(file?.status ?? '');

const ExportedFiles: React.FC<ExportedFilesProps> = ({ projectId, showDeletedItems = false, grids = [] }) => {
  const [selectedGridId, setSelectedGridId] = useState('');
  const gridOptions = useMemo(
    () => grids.filter((grid) => showDeletedItems || !grid.deletedAt),
    [grids, showDeletedItems]
  );
  const gridNames = useMemo(
    () => new Map(grids.map((grid) => [String(grid.id), grid.name || 'Unnamed grid'])),
    [grids]
  );
  useEffect(() => {
    if (selectedGridId && !gridOptions.some((grid) => String(grid.id) === selectedGridId)) {
      setSelectedGridId('');
    }
  }, [gridOptions, selectedGridId]);
  const loadFiles = useCallback(
    (currentProjectId: string) => getProjectExportedFiles(currentProjectId, selectedGridId || undefined),
    [selectedGridId]
  );

  return (
    <GridFilesPanel
      title="Exported Files"
      currentGridId={projectId}
      listEmptyMessage="No exported files found."
      filteredEmptyMessage="No exported files match the selected types."
      dateLabel="Exported"
      loadFiles={loadFiles}
      loadFileDetail={getProjectExportedFile}
      renameFile={updateProjectExportedFile}
      deleteFile={deleteProjectExportedFile}
      showDeletedFiles={showDeletedItems}
      extraColumnLabel="Grid"
      getExtraColumnValue={(file) => gridNames.get(String(file.gridId)) ?? '—'}
      headerActions={
        <FormControl className="files-filter-select" sx={{ minWidth: 220 }} size="small" disabled={!gridOptions.length}>
          <InputLabel id="exported-file-grid-filter-label">Filter by grid</InputLabel>
          <Select
            labelId="exported-file-grid-filter-label"
            label="Filter by grid"
            value={selectedGridId}
            onChange={(event) => setSelectedGridId(String(event.target.value))}
          >
            <MenuItem value="">All grids</MenuItem>
            {gridOptions.map((grid) => (
              <MenuItem key={grid.id} value={String(grid.id)}>
                {grid.name || 'Unnamed grid'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      }
      canDownload={canDownload}
      typeMetadata={TYPE_METADATA}
      downloadFile={async (_gridId, file, detail) => {
        const payload = detail ?? file;
        const downloadUrl = payload?.downloadUrl;

        if (!downloadUrl) {
          throw new Error('Download URL missing');
        }

        const fileResponse = await fetch(downloadUrl, { credentials: 'include' });

        if (!fileResponse.ok) {
          throw new Error(`Failed to download file: ${fileResponse.status}`);
        }

        triggerBlobDownload(await fileResponse.blob(), buildDownloadFileName(payload));
      }}
    />
  );
};

export default ExportedFiles;
